import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type {
  V1Queue,
  QueueAgentEntry,
  Agent,
  AgentStatus,
  QueuedCall,
  CustomStatus,
  WSAgentEvent,
  WSQueueEvent,
  WSQueuedEvent,
} from '@/types';
import { useUserStore } from './user';

export const useAgentStore = defineStore('agent', () => {
  const userStore = useUserStore();

  // State — v1 data model: array of queues, each with embedded agent entry
  const queues = ref<V1Queue[]>([]);
  const queuedCalls = ref<Map<string, QueuedCall[]>>(new Map());
  const customStatuses = ref<CustomStatus[]>([]);
  const portalStatus = ref('');
  const responders = ref<Map<string, Agent[]>>(new Map());

  // Pending action guard — prevents WS events from reverting optimistic UI updates
  const pendingAction = ref<string | null>(null);
  let pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  function setPendingAction(action: string | null, timeoutMs = 10000) {
    pendingAction.value = action;
    if (pendingTimeout) clearTimeout(pendingTimeout);
    if (action) {
      pendingTimeout = setTimeout(() => {
        pendingAction.value = null;
        pendingTimeout = null;
      }, timeoutMs);
    }
  }

  function clearPendingAction() {
    pendingAction.value = null;
    if (pendingTimeout) {
      clearTimeout(pendingTimeout);
      pendingTimeout = null;
    }
  }

  // Derived status — mirrors webphone's processAgentStatus logic
  const derivedStatus = computed<AgentStatus>(() => {
    console.debug('[Agent Store] Computing derivedStatus:', {
      queuesLength: queues.value.length,
      portalStatus: portalStatus.value,
      pendingAction: pendingAction.value,
      queues: queues.value.map(q => ({
        name: q.queue_name,
        entry_status: q.agent.entry_status,
        entry_option: q.agent.entry_option
      }))
    });

    // If there's a pending action, return the expected target status
    // to prevent flickering while waiting for server propagation
    if (pendingAction.value) {
      switch (pendingAction.value) {
        case 'login': return 'Online';
        case 'onecall': return 'SingleCall';
        case 'logout': return 'Offline';
        case 'custom': return portalStatus.value || 'Offline';
      }
    }

    if (queues.value.length === 0) return 'Offline';

    const onlineQueues = queues.value.filter(
      (q) => q.agent.entry_status === 'available' || q.agent.entry_option === 'immediate'
    );

    if (onlineQueues.length > 0) {
      if (onlineQueues.some((q) => q.agent.entry_option === 'manual')) {
        return 'SingleCall';
      }
      return 'Online';
    }

    if (portalStatus.value) return portalStatus.value;
    return 'Offline';
  });

  const isOnline = computed(() => derivedStatus.value === 'Online' || derivedStatus.value === 'SingleCall');

  // Initialize by fetching from NS v1 API (same approach as webphone)
  async function initializeFromNs() {
    const { nsApi } = await import('@/services/ns-api');

    async function fetchData(attempt = 1): Promise<void> {
      const [v1Queues, statuses] = await Promise.all([
        nsApi.getAgentQueues(),
        nsApi.getCustomStatuses(),
      ]);

      customStatuses.value = statuses;
      queues.value = v1Queues as V1Queue[];

      // If queues came back empty and this is an early attempt, retry after a delay.
      // This handles the race condition where TLS registration hasn't completed yet
      // or the backend worker was momentarily busy.
      if (queues.value.length === 0 && attempt < 3) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        return fetchData(attempt + 1);
      }
    }

    try {
      await fetchData();
    } catch (err) {
      const { useConnectionStore } = await import('./connection');
      const connectionStore = useConnectionStore();
      connectionStore.addAlert({
        type: 'api-error',
        severity: 'error',
        message: `Failed to load queue data: ${err instanceof Error ? err.message : 'Unknown error'}`,
        retryable: true,
      });
    }
  }

  // Fetch responders (all agents) for a specific queue
  async function fetchResponders(queueId: string) {
    const { nsApi } = await import('@/services/ns-api');
    try {
      const agents = await nsApi.getQueueAgents(queueId);

      // If there's a pending action, override our own agent's status in the response
      // to prevent showing stale data from the server while it's still processing
      if (pendingAction.value) {
        const myUsername = userStore.username;
        const myAgent = agents.find(a => a['callqueue-agent-id'] === myUsername);
        if (myAgent) {
          if (pendingAction.value === 'logout') {
            myAgent['callqueue-agent-availability-for-dispatch'] = 'unavailable';
            myAgent['callqueue-agent-availability-type'] = 'manual';
            myAgent.sub_portal_status = '';
          } else if (pendingAction.value === 'custom') {
            myAgent['callqueue-agent-availability-for-dispatch'] = 'unavailable';
            myAgent['callqueue-agent-availability-type'] = 'manual';
            myAgent.sub_portal_status = portalStatus.value;
          } else if (pendingAction.value === 'login') {
            myAgent['callqueue-agent-availability-for-dispatch'] = 'available';
            myAgent['callqueue-agent-availability-type'] = 'automatic';
            myAgent.sub_portal_status = '';
          } else if (pendingAction.value === 'onecall') {
            myAgent['callqueue-agent-availability-for-dispatch'] = 'available';
            myAgent['callqueue-agent-availability-type'] = 'manual';
            myAgent.sub_portal_status = '';
          }
        }
      }

      const updated = new Map(responders.value);
      updated.set(queueId, agents);
      responders.value = updated;
    } catch {
      // Silently fail — responders are non-critical
    }
  }

  // Debounced fetchResponders — batches multiple WS events into one API call per queue
  const fetchRespondersTimers = new Map<string, ReturnType<typeof setTimeout>>();
  function debouncedFetchResponders(queueId: string) {
    // Skip fetching while a status change is pending — schedulePostActionRefresh handles it
    if (pendingAction.value) return;

    const existing = fetchRespondersTimers.get(queueId);
    if (existing) clearTimeout(existing);
    fetchRespondersTimers.set(queueId, setTimeout(() => {
      fetchRespondersTimers.delete(queueId);
      fetchResponders(queueId);
    }, 2000));
  }

  // WebSocket event handlers

  /** Handle 'agent' WS event — updates per-queue agent entry */
  function handleAgentUpdate(data: WSAgentEvent) {
    const queueId = data.huntgroup_name;
    const idx = queues.value.findIndex((q) => q.queue_name === queueId);
    if (idx === -1) return;

    // Update our own agent entry if this event is for us
    const queue = queues.value[idx];
    const storedAor = queue.agent.device_aor;
    const eventAor = data.device_aor.replace(/^sip:/, '');
    if (storedAor === eventAor || storedAor === data.device_aor) {
      // If there's a pending status change, don't let WS events revert our optimistic update
      if (pendingAction.value) {
        console.debug('[Agent Store] Ignoring WS agent event during pending action:', pendingAction.value, 'queue:', queueId);
        // Still re-fetch responders but don't update our own entry
        debouncedFetchResponders(queueId);
        return;
      }

      // Update agent entry immutably
      const updatedAgent: QueueAgentEntry = { ...queue.agent };

      if (data.entry_option !== undefined) {
        updatedAgent.entry_option = data.entry_option as QueueAgentEntry['entry_option'];
      }
      if (data.entry_status !== undefined) {
        updatedAgent.entry_status = data.entry_status as QueueAgentEntry['entry_status'];
      }
      if (data.session_count !== undefined) {
        updatedAgent.session_count = String(data.session_count);
      }

      // Replace queue in array for reactivity
      const updatedQueues = [...queues.value];
      updatedQueues[idx] = { ...queue, agent: updatedAgent };
      queues.value = updatedQueues;

      // Update portal status
      if (data.sub_portal_status !== undefined) {
        portalStatus.value = data.sub_portal_status;
      }
    }

    // Re-fetch responders for this queue to update the detail card
    debouncedFetchResponders(queueId);
  }

  /** Handle 'queue' WS event — updates counts */
  function handleQueueUpdate(data: WSQueueEvent) {
    const idx = queues.value.findIndex((q) => q.queue_name === data.user);
    if (idx === -1) return;

    const queue = { ...queues.value[idx] };

    if (data.gau_queue_length !== undefined) {
      queue.queuedcall_count = Number(data.gau_queue_length);
    }
    if (data.gau_agent_available !== undefined) {
      queue.agent_available_count = Number(data.gau_agent_available);
    }
    if (data.gau_agent_logged_in !== undefined) {
      queue.agent_count = Number(data.gau_agent_logged_in);
    }

    const updatedQueues = [...queues.value];
    updatedQueues[idx] = queue;
    queues.value = updatedQueues;
  }

  /** Handle 'queued' WS event — add/remove waiting calls */
  function handleQueuedUpdate(data: WSQueuedEvent) {
    const queueId = data.queue_name;
    const updated = new Map(queuedCalls.value);

    if (data.remove === 'yes') {
      const current = updated.get(queueId) || [];
      updated.set(queueId, current.filter((c) => c.session_key !== data.session_key));
    } else {
      const current = updated.get(queueId) || [];
      const existing = current.findIndex((c) => c.session_key === data.session_key);

      if (existing === -1) {
        updated.set(queueId, [...current, data as unknown as QueuedCall]);
      } else {
        const arr = [...current];
        arr[existing] = data as unknown as QueuedCall;
        updated.set(queueId, arr);
      }
    }

    queuedCalls.value = updated;
  }

  /** Handle 'contacts-domain' WS event — portal status change */
  function handlePortalStatus(data: { user: string; portal_status: string }) {
    console.debug('[Agent Store] Portal status update:', data, 'current user:', userStore.user);
    if (data.user === userStore.user) {
      // Don't let WS events revert portal status during a pending status change
      if (pendingAction.value) {
        console.debug('[Agent Store] Ignoring portal status WS event during pending action:', pendingAction.value);
        return;
      }
      console.debug('[Agent Store] Setting portalStatus to:', data.portal_status);
      portalStatus.value = data.portal_status;
    } else {
      console.debug('[Agent Store] Portal status event for different user, ignoring');
    }
  }

  return {
    queues,
    queuedCalls,
    customStatuses,
    portalStatus,
    derivedStatus,
    isOnline,
    responders,
    pendingAction,
    setPendingAction,
    clearPendingAction,
    initializeFromNs,
    fetchResponders,
    handleAgentUpdate,
    handleQueueUpdate,
    handleQueuedUpdate,
    handlePortalStatus,
  };
});
